import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import { API, getJwt, fileSize } from './api'

/**
 * Gestionnaire de téléversements global :
 * - les envois continuent pendant la navigation dans l'app (navigation côté client) ;
 * - un panneau en bas à droite montre la progression ;
 * - les gros fichiers partent en morceaux de 8 Mo envoyés en parallèle (Cloudflare limite
 *   chaque requête à 100 Mo et bride chaque connexion), puis sont assemblés côté serveur.
 */
export interface UploadJob { id: number; label: string; size: number; pct: number; status: 'en cours' | 'terminé' | 'erreur' | 'annulé'; error?: string; speed?: number; abort?: () => void }

interface Ctx {
  jobs: UploadJob[]
  start: (path: string, files: File[], label?: string) => Promise<any>
  cancel: (id: number) => void
  clearDone: () => void
}
const UploadsCtx = createContext<Ctx>(null as any)
export const useUploads = () => useContext(UploadsCtx)

const CHUNK = 8 * 1024 * 1024        // 8 Mo par morceau
const CHUNK_THRESHOLD = 16 * 1024 * 1024 // en dessous : envoi simple
const PARALLEL = 4                    // morceaux simultanés
let seq = 0

function xhrSend(url: string, body: Document | XMLHttpRequestBodyInit | null, jwt: string, onProgress: (loaded: number) => void, register: (x: XMLHttpRequest) => void, contentType?: string) {
  return new Promise<any>((resolve, reject) => {
    const xhr = new XMLHttpRequest(); register(xhr)
    xhr.open('POST', url)
    xhr.setRequestHeader('Authorization', `Bearer ${jwt}`)
    if (contentType) xhr.setRequestHeader('Content-Type', contentType)
    xhr.upload.onprogress = e => onProgress(e.loaded)
    xhr.onload = () => { let d: any = {}; try { d = JSON.parse(xhr.responseText) } catch {} ; xhr.status < 300 ? resolve(d) : reject(new Error(d.error || `Erreur ${xhr.status}`)) }
    xhr.onerror = () => reject(new Error('Erreur réseau'))
    xhr.onabort = () => reject(new Error('Téléversement annulé'))
    xhr.send(body)
  })
}

export function UploadProvider({ children }: { children: ReactNode }) {
  const [jobs, setJobs] = useState<UploadJob[]>([])
  const patch = (id: number, p: Partial<UploadJob>) => setJobs(js => js.map(j => j.id === id ? { ...j, ...p } : j))

  const start = useCallback(async (path: string, files: File[], label?: string) => {
    const id = ++seq
    const size = files.reduce((s, f) => s + f.size, 0)
    setJobs(js => [...js, { id, label: label || (files.length === 1 ? files[0].name : `${files.length} fichiers`), size, pct: 0, status: 'en cours' }])
    const live = new Set<XMLHttpRequest>(); let aborted = false
    const abort = () => { aborted = true; live.forEach(x => x.abort()) }
    patch(id, { abort })
    const t0 = Date.now(); let doneBytes = 0
    const progress = (extra: number) => { const loaded = doneBytes + extra; patch(id, { pct: Math.min(99, Math.round(loaded / size * 100)), speed: loaded / ((Date.now() - t0) / 1000 || 1) }) }
    const sep = path.includes('?') ? '&' : '?'
    const merged: any = { files: [] }

    try {
      const jwt = await getJwt()
      for (const file of files) {
        if (aborted) throw new Error('Téléversement annulé')
        let result: any
        if (file.size <= CHUNK_THRESHOLD) {
          const fd = new FormData(); fd.append('file', file, file.name)
          result = await xhrSend(`${API}${path}`, fd, jwt, l => progress(l), x => live.add(x))
        } else {
          const uid = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`
          const n = Math.ceil(file.size / CHUNK)
          const sent: number[] = new Array(n).fill(0)
          let next = 0
          const worker = async () => {
            while (next < n && !aborted) {
              const i = next++
              const blob = file.slice(i * CHUNK, Math.min(file.size, (i + 1) * CHUNK))
              let tries = 0
              for (;;) {
                try {
                  await xhrSend(`${API}/chunk?upload=${uid}&index=${i}&chunk=${CHUNK}&total=${file.size}`, blob, jwt,
                    l => { sent[i] = l; progress(sent.reduce((a, b) => a + b, 0)) }, x => live.add(x), 'application/octet-stream')
                  sent[i] = blob.size; break
                } catch (e) { if (aborted || ++tries > 3) throw e; await new Promise(r => setTimeout(r, 1500 * tries)) }
              }
            }
          }
          await Promise.all(Array.from({ length: Math.min(PARALLEL, n) }, worker))
          if (aborted) throw new Error('Téléversement annulé')
          result = await xhrSend(`${API}${path}${sep}assemble=${uid}&filename=${encodeURIComponent(file.name)}&size=${file.size}`, null, jwt, () => {}, x => live.add(x))
        }
        doneBytes += file.size; progress(0)
        Object.assign(merged, result, { files: [...merged.files, ...(result.files || [])] })
      }
      patch(id, { pct: 100, status: 'terminé' })
      return merged
    } catch (e: any) {
      patch(id, { status: aborted ? 'annulé' : 'erreur', error: aborted ? undefined : e.message })
      throw e
    }
  }, [])

  const cancel = useCallback((id: number) => setJobs(js => { js.find(j => j.id === id)?.abort?.(); return js }), [])
  const clearDone = useCallback(() => setJobs(js => js.filter(j => j.status === 'en cours')), [])

  const active = jobs.some(j => j.status === 'en cours')
  useEffect(() => {
    if (!active) return
    const h = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = '' }
    window.addEventListener('beforeunload', h); return () => window.removeEventListener('beforeunload', h)
  }, [active])

  return (
    <UploadsCtx.Provider value={{ jobs, start, cancel, clearDone }}>
      {children}
      <UploadTray />
    </UploadsCtx.Provider>
  )
}

const speedFmt = (b?: number) => !b ? '' : b > 1e6 ? (b / 1e6).toFixed(1) + ' Mo/s' : Math.round(b / 1e3) + ' Ko/s'

function UploadTray() {
  const { jobs, cancel, clearDone } = useUploads()
  const [open, setOpen] = useState(true)
  if (!jobs.length) return null
  const active = jobs.filter(j => j.status === 'en cours')
  return (
    <div className="fixed bottom-4 right-4 z-40 w-80 max-w-[calc(100vw-2rem)] bg-noir border border-white/15 rounded shadow-2xl text-sm">
      <div className="flex items-center justify-between px-4 py-2 border-b border-white/10 cursor-pointer" onClick={() => setOpen(o => !o)}>
        <span className="text-xs uppercase tracking-widest">{active.length ? `⬆ ${active.length} téléversement${active.length > 1 ? 's' : ''} en cours` : 'Téléversements terminés'}</span>
        <span className="text-acier">{open ? '▾' : '▴'}</span>
      </div>
      {open && (
        <div className="max-h-64 overflow-y-auto">
          {jobs.map(j => {
            const remaining = j.speed && j.status === 'en cours' ? Math.max(0, Math.round((j.size * (1 - j.pct / 100)) / j.speed)) : 0
            return (
              <div key={j.id} className="px-4 py-2 border-t border-white/5 first:border-0">
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate">{j.status === 'terminé' ? '✅' : j.status === 'erreur' ? '❌' : j.status === 'annulé' ? '⏹' : '⬆'} {j.label}</span>
                  <span className="text-xs text-acier whitespace-nowrap">{j.status === 'en cours' ? `${j.pct}%` : fileSize(j.size)}</span>
                </div>
                {j.status === 'en cours' && <>
                  <div className="h-1 bg-white/10 mt-1 rounded"><div className="h-1 bg-orange rounded transition-all" style={{ width: j.pct + '%' }} /></div>
                  <div className="flex justify-between text-[10px] text-acier mt-1">
                    <span>{speedFmt(j.speed)}{remaining ? ` · ~${remaining > 90 ? Math.round(remaining / 60) + ' min' : remaining + ' s'} restantes` : ''}</span>
                    <button onClick={() => cancel(j.id)} className="uppercase tracking-widest hover:text-red-300">Annuler</button>
                  </div>
                </>}
                {j.error && <div className="text-xs text-red-300 mt-1">{j.error}</div>}
              </div>
            )
          })}
          {jobs.length > active.length && <button onClick={clearDone} className="w-full text-[10px] uppercase tracking-widest text-acier hover:text-orange py-2 border-t border-white/10">Effacer les terminés</button>}
        </div>
      )}
    </div>
  )
}
